import React, { useState, useEffect } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { BrandIntro } from '../brand/BrandIntro';
import { api } from '../../services/api';
import type { SiteSettings } from '../../types/api';

export const PublicLayout: React.FC = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [settings, setSettings] = useState<SiteSettings>({});
    const location = useLocation();

    useEffect(() => {
        api.getSettings().then(res => {
            if (res.data) setSettings(res.data);
        }).catch(() => {});
    }, []);

    // Close mobile menu on route change
    useEffect(() => {
        setMobileMenuOpen(false);
        window.scrollTo(0, 0);
    }, [location.pathname]);

    const logoUrl = settings.brand_logo ? `/storage/${settings.brand_logo}` : "/Rakib's logo.jpg";
    const introEnabled = settings.intro_enabled !== false && settings.intro_enabled !== 'false';
    const email = settings.contact_email || 'rakibh6970@gmail.com';
    const whatsapp = settings.contact_whatsapp || 'https://wa.me/8801762405538';
    const facebook = settings.social_facebook || 'https://www.facebook.com/share/1CK6QxzTH5/';
    const instagram = settings.social_instagram || 'https://www.instagram.com/rakib__unscripted?xtok=cm9hcnd1cHV0ODdy';

    return (
        <div className="min-h-screen flex flex-col font-sans selection:bg-emerald-500 selection:text-white bg-slate-950 text-slate-100">
            {/* GSAP Motion Intro */}
            <BrandIntro enabled={introEnabled} logoUrl={logoUrl} />

            {/* Navigation Bar */}
            <header className="sticky top-0 z-40 w-full glass-dark border-b border-slate-800/80">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-20">
                        {/* Brand Logo & Title */}
                        <Link to="/" className="flex items-center gap-3.5 group">
                            <div className="w-12 h-12 rounded-full overflow-hidden ring-2 ring-emerald-500/50 group-hover:ring-emerald-400 transition-all p-0.5 bg-slate-900 shrink-0 shadow-md">
                                <img src={logoUrl} alt="Rakib's Care Logo" className="w-full h-full object-cover rounded-full" />
                            </div>
                            <div>
                                <span className="text-xl font-bold tracking-tight text-white block group-hover:text-emerald-400 transition">
                                    Rakib's <span className="text-emerald-400">Care</span>
                                </span>
                                <span className="text-[11px] text-slate-400 font-medium tracking-wider uppercase block -mt-0.5">
                                    Rakib Hasan
                                </span>
                            </div>
                        </Link>

                        {/* Desktop Navigation Links */}
                        <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-300">
                            <NavLink 
                                to="/" 
                                end
                                className={({ isActive }) => `hover:text-emerald-400 transition ${isActive ? 'text-emerald-400 font-semibold' : ''}`}
                            >
                                Home
                            </NavLink>
                            <NavLink 
                                to="/about" 
                                className={({ isActive }) => `hover:text-emerald-400 transition ${isActive ? 'text-emerald-400 font-semibold' : ''}`}
                            >
                                About Rakib
                            </NavLink>
                            <NavLink 
                                to="/leadership" 
                                className={({ isActive }) => `hover:text-emerald-400 transition ${isActive ? 'text-emerald-400 font-semibold' : ''}`}
                            >
                                Leadership & Activities
                            </NavLink>
                            <NavLink 
                                to="/blog" 
                                className={({ isActive }) => `hover:text-emerald-400 transition ${isActive ? 'text-emerald-400 font-semibold' : ''}`}
                            >
                                Perspectives
                            </NavLink>
                            <NavLink 
                                to="/contact" 
                                className={({ isActive }) => `hover:text-emerald-400 transition ${isActive ? 'text-emerald-400 font-semibold' : ''}`}
                            >
                                Contact
                            </NavLink>
                        </nav>

                        {/* Actions */}
                        <div className="hidden md:flex items-center gap-4">
                            <Link 
                                to="/contact" 
                                className="inline-flex items-center px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-900/40 hover:shadow-emerald-700/50 transition cursor-pointer"
                            >
                                Connect With Rakib
                            </Link>
                        </div>

                        {/* Mobile Hamburger Button */}
                        <div className="flex md:hidden">
                            <button 
                                id="mobile-menu-btn" 
                                type="button" 
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                className="text-slate-400 hover:text-white p-2 rounded-lg cursor-pointer" 
                                aria-label="Toggle Navigation Menu"
                            >
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={mobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16m-7 6h7"} />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile Drawer */}
                {mobileMenuOpen && (
                    <div id="mobile-menu-drawer" className="md:hidden bg-slate-900/95 border-b border-slate-800 px-4 pt-3 pb-6 space-y-3">
                        <Link to="/" className="block px-3 py-2 rounded-md text-base font-medium text-slate-200 hover:bg-slate-800 hover:text-emerald-400">Home</Link>
                        <Link to="/about" className="block px-3 py-2 rounded-md text-base font-medium text-slate-200 hover:bg-slate-800 hover:text-emerald-400">About Rakib</Link>
                        <Link to="/leadership" className="block px-3 py-2 rounded-md text-base font-medium text-slate-200 hover:bg-slate-800 hover:text-emerald-400">Leadership & Activities</Link>
                        <Link to="/blog" className="block px-3 py-2 rounded-md text-base font-medium text-slate-200 hover:bg-slate-800 hover:text-emerald-400">Perspectives</Link>
                        <Link to="/contact" className="block px-3 py-2 rounded-md text-base font-medium text-slate-200 hover:bg-slate-800 hover:text-emerald-400">Contact</Link>
                        <div className="pt-2">
                            <Link to="/contact" className="block w-full text-center px-4 py-2.5 rounded-full text-sm font-semibold bg-emerald-600 hover:bg-emerald-500 text-white shadow-md">
                                Connect With Rakib
                            </Link>
                        </div>
                    </div>
                )}
            </header>

            {/* Main Page Content */}
            <main className="flex-grow">
                <Outlet />
            </main>

            {/* Footer */}
            <footer className="bg-slate-950 border-t border-slate-800/80 text-slate-400 pt-16 pb-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-10 pb-12 border-b border-slate-800/80">
                        {/* Col 1: Brand & Bio */}
                        <div className="md:col-span-2 space-y-4">
                            <div className="flex items-center gap-3">
                                <img src={logoUrl} alt="Rakib's Care Logo" className="w-10 h-10 rounded-full object-cover ring-2 ring-emerald-500/40" />
                                <span className="text-xl font-bold tracking-tight text-white">Rakib's <span className="text-emerald-400">Care</span></span>
                            </div>
                            <p className="text-sm text-slate-400 max-w-md leading-relaxed">
                                The official personal platform of Rakib Hasan. Student leader, organizer, and youth advocate from Habiganj, Bangladesh. Dedicated to community service, structured student governance, and youth empowerment.
                            </p>
                            <div className="flex items-center gap-3 pt-2">
                                {/* Email */}
                                {email && (
                                    <a 
                                        href={`mailto:${email}`} 
                                        className="w-10 h-10 rounded-full bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:text-emerald-400 hover:border-emerald-500/50 hover:bg-slate-800 transition shadow-sm" 
                                        title={`Email: ${email}`} 
                                        aria-label="Email Rakib Hasan"
                                    >
                                        <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>
                                    </a>
                                )}

                                {/* WhatsApp */}
                                {whatsapp && (
                                    <a 
                                        href={whatsapp} 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        className="w-10 h-10 rounded-full bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:text-emerald-400 hover:border-emerald-500/50 hover:bg-slate-800 transition shadow-sm" 
                                        title="WhatsApp" 
                                        aria-label="Chat on WhatsApp"
                                    >
                                        <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>
                                    </a>
                                )}

                                {/* Facebook */}
                                {facebook && (
                                    <a 
                                        href={facebook} 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        className="w-10 h-10 rounded-full bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:text-emerald-400 hover:border-emerald-500/50 hover:bg-slate-800 transition shadow-sm" 
                                        title="Facebook" 
                                        aria-label="Facebook Profile"
                                    >
                                        <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                                    </a>
                                )}

                                {/* Instagram */}
                                {instagram && (
                                    <a 
                                        href={instagram} 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        className="w-10 h-10 rounded-full bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:text-emerald-400 hover:border-emerald-500/50 hover:bg-slate-800 transition shadow-sm" 
                                        title="Instagram" 
                                        aria-label="Instagram Profile"
                                    >
                                        <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                                    </a>
                                )}
                            </div>
                        </div>

                        {/* Col 2: Navigation Links */}
                        <div>
                            <h2 className="text-xs font-bold text-white uppercase tracking-wider mb-4">Navigation</h2>
                            <ul className="space-y-2.5 text-sm">
                                <li><Link to="/" className="hover:text-emerald-400 transition">Home</Link></li>
                                <li><Link to="/about" className="hover:text-emerald-400 transition">About Rakib</Link></li>
                                <li><Link to="/leadership" className="hover:text-emerald-400 transition">Leadership & Activities</Link></li>
                                <li><Link to="/blog" className="hover:text-emerald-400 transition">Perspectives & Articles</Link></li>
                                <li><Link to="/contact" className="hover:text-emerald-400 transition">Get in Touch</Link></li>
                            </ul>
                        </div>

                        {/* Col 3: Verified Affiliations */}
                        <div>
                            <h2 className="text-xs font-bold text-white uppercase tracking-wider mb-4">Key Roles</h2>
                            <ul className="space-y-2.5 text-sm text-slate-400">
                                <li><span className="text-slate-200">President</span> • English Knowledge Holders</li>
                                <li><span className="text-slate-200">Treasurer</span> • Interact Club of Habiganj</li>
                                <li><span className="text-slate-200">1st Leader</span> • Govt. High School Band</li>
                                <li><span className="text-slate-200">Charity Partner</span> • Magico'24</li>
                                <li><span className="text-slate-200">Treasurer</span> • Muktanchol</li>
                            </ul>
                        </div>
                    </div>

                    {/* Bottom Copyright */}
                    <div className="pt-8 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-400 gap-4">
                        <p>&copy; 2026-2027 Rakib's Care (Rakib Hasan). All rights reserved.</p>
                        <p className="text-slate-400">
                            Habiganj, Sylhet, Bangladesh • Developed By: <a 
                                href="https://deadleadsociety.com/genesis" 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-slate-300 hover:text-emerald-400 underline underline-offset-4 decoration-emerald-500/50 hover:decoration-emerald-400 transition font-medium"
                            >
                                Dead Lead Society
                            </a>
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};
